/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * UI domain - Performance statistics display with color-coded indicators
 * perfstats.ui.client.ts - Dedicated performance stats component with optimized DOM updates
 */

// Type definitions for performance stats component
export interface PerformanceStatsState {
  tps: number;
  frameTime: number;
  entities: {
    players: number;
    bullets: number;
    asteroids: number;
    pellets: number;
  };
  ping: number;
  lastUpdate: number;
}

export interface PerformanceElements {
  tpsEl: HTMLElement | null;
  frameEl: HTMLElement | null;
  entitiesEl: HTMLElement | null;
  netEl: HTMLElement | null;
}

// Component state
const elements: PerformanceElements = {
  tpsEl: null,
  frameEl: null,
  entitiesEl: null,
  netEl: null,
};

/**
 * Initialize performance stats UI component
 * Caches DOM element references for performance
 */
export function initializePerformanceStats(): void {
  elements.tpsEl = document.getElementById("perf-tps");
  elements.frameEl = document.getElementById("perf-frame");
  elements.entitiesEl = document.getElementById("perf-entities");
  elements.netEl = document.getElementById("perf-network");
}

/**
 * Update performance stats display
 * Updates all performance metrics with color-coded indicators
 */
export function updatePerformanceStats(state: PerformanceStatsState): void {
  updateTPS(state.tps);
  updateFrameTime(state.frameTime);
  updateEntityCounts(state.entities);
  updateNetworkLatency(state.ping);
}

/**
 * Update TPS (Ticks Per Second) display with color coding
 */
function updateTPS(tps: number): void {
  if (elements.tpsEl) {
    elements.tpsEl.textContent = tps.toString();
    elements.tpsEl.className =
      tps >= 55 ? "value good" : tps >= 45 ? "value warning" : "value bad";
  }
}

/**
 * Update frame time display with color coding
 */
function updateFrameTime(frameTime: number): void {
  if (elements.frameEl) {
    elements.frameEl.textContent = `${frameTime.toFixed(1)}ms`;
    elements.frameEl.className =
      frameTime < 10
        ? "value good"
        : frameTime < 20
          ? "value warning"
          : "value bad";
  }
}

/**
 * Update total entity count display
 */
function updateEntityCounts(entities: PerformanceStatsState["entities"]): void {
  if (elements.entitiesEl) {
    const totalEntities =
      entities.players +
      entities.bullets +
      entities.asteroids +
      entities.pellets;
    elements.entitiesEl.textContent = totalEntities.toString();
  }
}

/**
 * Update network latency display with color coding
 */
function updateNetworkLatency(ping: number): void {
  if (elements.netEl) {
    elements.netEl.textContent = `${ping}ms`;
    elements.netEl.className =
      ping < 50 ? "value good" : ping < 100 ? "value warning" : "value bad";
  }
}

/**
 * Get current performance elements (for debugging)
 */
export function getPerformanceElements(): PerformanceElements {
  return elements;
}
